import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaSearch, FaHeart, FaHeartBroken, FaFileExcel, FaFileCsv, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';
import { useParcelContext } from '../context/ParcelContext';
import { useFavorites } from '../context/FavoritesContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Utility functions for proper capitalization
const capitalizeStateCode = (stateCode) => {
  return stateCode ? stateCode.toUpperCase() : '';
};

const capitalizeName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

const DetailsContainer = styled.div`
  flex: 1;
  padding: 1rem;
  background: white;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #667eea;
`;

const DetailsTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: #333;
  }
`;

const DetailsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0 0 0;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  color: #333;
  font-size: 1rem;
  border-bottom: 2px solid #667eea;
  padding-bottom: 0.25rem;
`;

const DetailField = styled.div`
  margin: 0.5rem 0;
  font-size: 0.9rem;
  
  strong {
    color: #666;
    font-weight: 600;
    display: inline-block;
    min-width: 120px;
  }
`;

const FavoriteButton = styled.button.attrs(props => ({
  isFavorite: undefined
}))`
  background: ${props => props.isFavorite ? '#ff4757' : '#fff'};
  color: ${props => props.isFavorite ? '#fff' : '#333'};
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  margin-top: 1rem;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: ${props => props.isFavorite ? '#ff3742' : '#f5f5f5'};
  }
`;

const NoSelectionMessage = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 2rem;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: ${props => props.active ? '#667eea' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : props.disabled ? '#ccc' : '#333'};
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.9rem;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background: ${props => props.active ? '#5a6fd8' : props.disabled ? '#f8f9fa' : '#e9e9e9'};
  }
`;

const ExportButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ExportButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  
  &:hover {
    background: #e9e9e9;
  }
`;

const SummaryCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const SummaryTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1rem;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const SummaryField = styled.div`
  strong {
    color: #666;
  }
`;

const ResultItem = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fafafa;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f0f0f0;
    border-color: #667eea;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const ItemTitle = styled.h4`
  margin: 0;
  color: #333;
  font-size: 1rem;
`;

const AddToFavoritesButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover {
    background: #ff3742;
  }
`;

const ItemDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

const ResultDetailField = styled.div`
  strong {
    color: #666;
  }
`;

const TypeBadge = styled.span`
  background: ${props => props.type === 'parcel' ? '#667eea' : '#2ed573'};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.3;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PaginationInfo = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.disabled ? '#f5f5f5' : '#667eea'};
  color: ${props => props.disabled ? '#ccc' : 'white'};
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#5a6fd8'};
  }
`;

const ParcelDetailsPanel = ({ isOpen, onClose, setViewState }) => {
  const { 
    selectedParcel, 
    filteredParcels,
    assemblages,
    searchSummary,
    totalCount,
    loading,
    loadingParcelDetails,
    filters,
    setSelectedParcel
  } = useParcelContext();
  
  const { 
    favorites,
    createFavorite,
    deleteFavorite,
    checkFavoriteExists,
    getFavorites
  } = useFavorites();

  const [activeTab, setActiveTab] = useState('details');
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 50;

  // Load favorites on component mount
  useEffect(() => {
    getFavorites();
  }, [getFavorites]);

  // Calculate results early
  const results = filters.filterType === 'single' ? filteredParcels : assemblages;
  const resultType = filters.filterType === 'single' ? 'parcel' : 'assemblage';

  // Auto-switch to results tab when no parcel is selected
  useEffect(() => {
    if (!selectedParcel && activeTab === 'details') {
      setActiveTab('details');
    }
  }, [selectedParcel, activeTab]);

  // Reset to first page when results change
  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  const handleFavoriteToggle = async (item) => {
    try {
      // Normalize the item's parcel IDs for comparison
      let itemParcelIds = [];
      if (item.parcel_id) {
        // Single parcel - convert number to string
        itemParcelIds = [String(item.parcel_id)];
      } else if (item.parcel_ids) {
        // Could be JSON string or array
        if (typeof item.parcel_ids === 'string') {
          try {
            itemParcelIds = JSON.parse(item.parcel_ids);
          } catch {
            itemParcelIds = [item.parcel_ids];
          }
        } else if (Array.isArray(item.parcel_ids)) {
          itemParcelIds = item.parcel_ids.map(id => String(id));
        } else {
          itemParcelIds = [String(item.parcel_ids)];
        }
      }
      
      // Check if this item is already favorited
      const existingFavorite = favorites.find(fav => {
        // Normalize favorite's parcel IDs for comparison
        let favParcelIds = [];
        if (typeof fav.parcel_ids === 'string') {
          // Could be a single parcel ID as string or JSON array as string
          try {
            favParcelIds = JSON.parse(fav.parcel_ids);
            if (!Array.isArray(favParcelIds)) {
              favParcelIds = [fav.parcel_ids];
            }
          } catch {
            favParcelIds = [fav.parcel_ids];
          }
        } else if (Array.isArray(fav.parcel_ids)) {
          favParcelIds = fav.parcel_ids;
        } else if (fav.parcel_ids) {
          favParcelIds = [fav.parcel_ids];
        }

        // Compare arrays
        if (itemParcelIds.length === 1 && favParcelIds.length === 1) {
          return itemParcelIds[0] === favParcelIds[0];
        } else if (itemParcelIds.length > 1 && favParcelIds.length > 1) {
          return JSON.stringify(itemParcelIds.sort()) === JSON.stringify(favParcelIds.sort());
        }
        return false;
      });

      if (existingFavorite) {
        // Remove from favorites
        await deleteFavorite(existingFavorite.id);
      } else {
        // Add to favorites
        console.log('Item:', item);
        const favoriteData = {
          parcel_ids: itemParcelIds,
          county_id: filters.county_id || 1, // Get county_id from filters or default to 1
          in_place_zoning: item.zoning || null,
          future_land_use: item.land_use_category || null,
          size_acres: item.land_acres || item.total_acres || null,
          total_improvement_value: item.assd_imp || null,
          center_point: item.center ? item.center.coordinates : null
        };

        console.log('Favorite data:', favoriteData);
        
        await createFavorite(favoriteData);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const isFavorite = (item) => {
    // Normalize the item's parcel IDs for comparison
    let itemParcelIds = [];
    if (item.parcel_id) {
      // Single parcel - convert number to string
      itemParcelIds = [String(item.parcel_id)];
    } else if (item.parcel_ids) {
      // Could be JSON string or array
      if (typeof item.parcel_ids === 'string') {
        try {
          itemParcelIds = JSON.parse(item.parcel_ids);
        } catch {
          itemParcelIds = [item.parcel_ids];
        }
      } else if (Array.isArray(item.parcel_ids)) {
        itemParcelIds = item.parcel_ids.map(id => String(id));
      } else {
        itemParcelIds = [String(item.parcel_ids)];
      }
    }
    
    return favorites.some(fav => {
      // Normalize favorite's parcel IDs for comparison
      let favParcelIds = [];
      if (typeof fav.parcel_ids === 'string') {
        // Could be a single parcel ID as string or JSON array as string
        try {
          favParcelIds = JSON.parse(fav.parcel_ids);
          if (!Array.isArray(favParcelIds)) {
            favParcelIds = [fav.parcel_ids];
          }
        } catch {
          favParcelIds = [fav.parcel_ids];
        }
      } else if (Array.isArray(fav.parcel_ids)) {
        favParcelIds = fav.parcel_ids;
      } else if (fav.parcel_ids) {
        favParcelIds = [fav.parcel_ids];
      }

      // Compare arrays
      if (itemParcelIds.length === 1 && favParcelIds.length === 1) {
        return itemParcelIds[0] === favParcelIds[0];
      } else if (itemParcelIds.length > 1 && favParcelIds.length > 1) {
        return JSON.stringify(itemParcelIds.sort()) === JSON.stringify(favParcelIds.sort());
      }
      return false;
    });
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      // Export all results, not just current page
      const worksheet = XLSX.utils.json_to_sheet(results.map(item => {
        if (resultType === 'parcel') {
          return {
            Type: 'Single Parcel',
            'Parcel Number': item.parcel_id,
            Size: `${item.land_acres} acres`,
            'Future Land Use': item.land_use_category,
            'Current Zoning': item.zoning,
            Address: item.addr,
            Owner: item.owner,
            'Assessed Value': `$${item.assd_imp?.toLocaleString()}`,
            'Assessed Value Per Acre': `$${(item.assd_imp / item.land_acres)?.toFixed(2)}`
          };
        } else {
          return {
            Type: 'Assemblage',
            'Parcel Numbers': item.parcel_ids?.join(', '),
            Size: `${item.total_acres} acres`,
            'Number of Parcels': item.numberOfParcels,
            'Future Land Use': item.land_use_category,
            'Current Zoning': item.zoning,
            Addresses: item.addr,
            Owners: item.owner,
            'Assessed Value': `$${item.assd_imp?.toLocaleString()}`,
            'Assessed Value Per Acre': `$${(item.assd_imp / item.total_acres)?.toFixed(2)}`
          };
        }
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Search Results');
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, 'parcel_search_results.xlsx');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = () => {
    setExporting(true);
    try {
      // Export all results, not just current page
      const csvContent = results.map(item => {
        if (resultType === 'parcel') {
          return `"Single Parcel","${item.parcel_id}","${item.land_acres} acres","${item.land_use_category}","${item.zoning}","${item.addr}","${item.owner}","$${item.assd_imp?.toLocaleString()}","$${(item.assd_imp / item.land_acres)?.toFixed(2)}"`;
        } else {
          return `"Assemblage","${item.parcel_ids?.join(', ')}","${item.total_acres} acres","${item.numberOfParcels}","${item.land_use_category}","${item.zoning}","${item.addr}","${item.owner}","$${item.assd_imp?.toLocaleString()}","$${(item.assd_imp / item.total_acres)?.toFixed(2)}"`;
        }
      }).join('\n');

      const headers = 'Type,Parcel Number,Size,Future Land Use,Current Zoning,Address,Owner,Assessed Value,Assessed Value Per Acre\n';
      const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'parcel_search_results.csv');
    } catch (error) {
      console.error('Error exporting to CSV:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleResultClick = (item) => {
    if (setViewState && item.center) {
      // Zoom to the parcel/assemblage coordinates at zoom level 16
      setViewState({
        longitude: item.center.coordinates[0],
        latitude: item.center.coordinates[1],
        zoom: 16,
        transitionDuration: 1000 // Smooth transition
      });
      
      // Also set this as the selected parcel
      if (item.parcel_id) { item.type = 'parcel' } else { item.type = 'assemblage' }
      console.log('Setting selected parcel:', item);
      setSelectedParcel(item);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(results.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = results.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of results
    const contentElement = document.querySelector('.details-content');
    if (contentElement) {
      contentElement.scrollTop = 0;
    }
  };



  return (
    <DetailsContainer isOpen={isOpen}>
      <DetailsHeader>
        <DetailsTitle>
          {selectedParcel ? 
            `${selectedParcel.type === 'parcel' ? '📋' : '🏢'} ${selectedParcel.type === 'parcel' ? 
              `Parcel #${selectedParcel.parcel_id}` : 
              `Assemblage #${selectedParcel.id || selectedParcel.cluster_id}`
            }` : 
            '📋 Details & Results'
          }
        </DetailsTitle>
        {/* CloseButton removed */}
      </DetailsHeader>
      
      <TabContainer>
        <Tab 
          active={activeTab === 'details'} 
          onClick={() => setActiveTab('details')}
          disabled={!selectedParcel}
        >
          Parcel Details
        </Tab>
        <Tab 
          active={activeTab === 'results'} 
          onClick={() => setActiveTab('results')}
        >
          Search Results ({results.length})
        </Tab>
      </TabContainer>
      
      <DetailsContent className="details-content">
        {activeTab === 'details' ? (
          <>
            {loadingParcelDetails ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <LoadingSpinner />
                <p>Loading detailed parcel information...</p>
              </div>
            ) : selectedParcel ? (
              <>
                <DetailSection>
                  <SectionTitle>Basic Information</SectionTitle>
                  <DetailField><strong>Type:</strong> {selectedParcel.type === 'parcel' ? 'Individual Parcel' : 'Assemblage'}</DetailField>
                  <DetailField><strong>Size:</strong> {selectedParcel.land_acres || selectedParcel.total_acres || 'N/A'} acres</DetailField>
                  {selectedParcel.type === 'parcel' ? (
                    <DetailField><strong>Parcel ID:</strong> {selectedParcel.parcel_id}</DetailField>
                  ) : (
                    <>
                      <DetailField><strong>Parcel IDs:</strong> { 
                        selectedParcel.parcel_ids ? 
                          (Array.isArray(selectedParcel.parcel_ids) ? 
                            selectedParcel.parcel_ids.join(', ') : 
                            (typeof selectedParcel.parcel_ids === 'string' ? 
                              JSON.parse(selectedParcel.parcel_ids).join(', ') : 
                              selectedParcel.parcel_ids)
                          ) : 'N/A'
                      }</DetailField>
                      <DetailField><strong>Number of Parcels:</strong> {selectedParcel.numberOfParcels}</DetailField>
                    </>
                  )}
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Actions</SectionTitle>
                  <FavoriteButton 
                    isFavorite={isFavorite(selectedParcel)}
                    onClick={() => handleFavoriteToggle(selectedParcel)}
                  >
                    {isFavorite(selectedParcel) ? <FaHeartBroken /> : <FaHeart />}
                    {isFavorite(selectedParcel) ? 'Remove from Favorites' : 'Add to Favorites'}
                  </FavoriteButton>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Land Use & Zoning</SectionTitle>
                  <DetailField><strong>Future Land Use:</strong> {selectedParcel.land_use_category || 'N/A'}</DetailField>
                  <DetailField><strong>Current Zoning:</strong> {selectedParcel.zoning || 'N/A'}</DetailField>
                </DetailSection>

                {selectedParcel.type === 'parcel' ? (
                  <DetailSection>
                    <SectionTitle>Property Details</SectionTitle>
                    <DetailField><strong>Total Improvement Value:</strong> ${(selectedParcel.assd_imp || 0)?.toLocaleString()}</DetailField>
                  </DetailSection>
                ) : (
                  <DetailSection>
                    <SectionTitle>Assemblage Details</SectionTitle>
                    <DetailField><strong>Total Improvement Value:</strong> ${(selectedParcel.assd_imp || 0)?.toLocaleString()}</DetailField>
                  </DetailSection>
                )}

                {/* Full Parcel Data Section */}
                {selectedParcel.type === 'parcel' && (
                  <DetailSection>
                    <SectionTitle>Full Parcel Data</SectionTitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {[
                        ['PARCEL ID', 'parcel_id'],
                        ['ASSESSOR PARCEL NUMBER', 'apn'],
                        ['ASSESSOR PARCEL NUMBER 2', 'apn2'],
                        ['ADDRESS', 'addr'],
                        ['CITY', 'city'],
                        ['STATE CODE', 'state'],
                        ['ZIP CODE', 'zip'],
                        ['UNFORMATTED APN', 'unfrm_apn'],
                        ['APN SEQUENCE NBR', 'apn_seq_no'],
                        ['FORMATTED APN', 'frm_apn'],
                        ['ORIGINAL APN', 'orig_apn'],
                        ['ACCOUNT NUMBER', 'acct_no'],
                        ['CENSUS TRACT', 'census_tr'],
                        ['ZONING', 'zoning'],
                        ['OWNER CORPORATE INDICATOR', 'own_cp_ind'],
                        ['OWNER 1 LAST NAME', 'own1_last'],
                        ['OWNER 1 FIRST NAME & M I', 'own1_frst'],
                        ['OWNER 2 LAST NAME', 'own2_last'],
                        ['OWNER 2 FIRST NAME & MI', 'own2_frst'],
                        ['MAIL HOUSE NUMBER PREFIX', 'mail_nbrpx'],
                        ['MAIL HOUSE NUMBER', 'mail_nbr'],
                        ['MAIL HOUSE NUMBER #2', 'mail_nbr2'],
                        ['MAIL HOUSE NUMBER SUFFIX', 'mail_nbrsx'],
                        ['MAIL DIRECTION', 'mail_dir'],
                        ['MAIL STREET NAME', 'mail_str'],
                        ['MAIL MODE', 'mail_mode'],
                        ['MAIL QUADRANT', 'mail_qdrt'],
                        ['MAIL UNIT NUMBER', 'mail_unit'],
                        ['MAIL CITY', 'mail_city'],
                        ['MAIL STATE', 'mail_state'],
                        ['MAIL ZIP CODE', 'mail_zip'],
                        ['MAIL CARRIER CODE', 'mail_cc'],
                        ['MAILING OPT-OUT CODE', 'mail_opt'],
                        ['TOTAL VALUE CALCULATED', 'tot_val'],
                        ['LAND VALUE CALCULATED', 'lan_val'],
                        ['IMPROVEMENT VALUE CALCULATED', 'imp_val'],
                        ['TOTAL VALUE CALCULATED IND', 'tot_val_cd'],
                        ['LAND VALUE CALCULATED IND', 'lan_val_cd'],
                        ['ASSD TOTAL VALUE', 'assd_val'],
                        ['ASSD LAND VALUE', 'assd_lan'],
                        ['ASSD IMPROVEMENT VALUE', 'assd_imp'],
                        ['MKT TOTAL VALUE', 'mkt_val'],
                        ['MKT LAND VALUE', 'mkt_lan'],
                        ['MKT IMPROVEMENT VALUE', 'mkt_imp'],
                        ['APPR TOTAL VALUE', 'appr_val'],
                        ['APPR LAND VALUE', 'appr_lan'],
                        ['APPR IMPROVEMENT VALUE', 'appr_imp'],
                        ['TAX AMOUNT', 'tax_amt'],
                        ['TAX YEAR', 'tax_yr'],
                        ['ASSESSED YEAR', 'assd_yr'],
                        ['TAX CODE AREA', 'tax_area'],
                        ['ACRES', 'land_acres'],
                        ['ELECTRIC/ENERGY', 'energy_use'],
                        ['FUEL', 'fuel'],
                        ['SEWER', 'sewer'],
                        ['WATER', 'water'],
                        ['LAND USE CATEGORY', 'land_use_category'],
                      ].map(([label, key]) => (
                        selectedParcel[key] !== undefined && selectedParcel[key] !== null && selectedParcel[key] !== '' ? (
                          <DetailField key={key}><strong>{label}:</strong> {selectedParcel[key]}</DetailField>
                        ) : null
                      ))}
                    </div>
                  </DetailSection>
                )}
              </>
            ) : (
              <NoSelectionMessage>
                Click on a parcel or assemblage on the map to view details, or switch to the Search Results tab
              </NoSelectionMessage>
            )}
          </>
        ) : (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <LoadingSpinner />
                <p>Searching parcels...</p>
              </div>
            ) : !results || results.length === 0 ? (
              <EmptyState>
                <EmptyIcon>
                  <FaSearch />
                </EmptyIcon>
                <p>No results found</p>
                <p>Try adjusting your search criteria</p>
              </EmptyState>
            ) : (
              <>
                <ExportButtons>
                  <ExportButton onClick={exportToExcel} disabled={exporting}>
                    <FaFileExcel />
                    Excel
                  </ExportButton>
                  <ExportButton onClick={exportToCSV} disabled={exporting}>
                    <FaFileCsv />
                    CSV
                  </ExportButton>
                </ExportButtons>

                {/* Search Summary */}
                {searchSummary && (
                  <SummaryCard>
                    <SummaryTitle>Search Summary</SummaryTitle>
                    <SummaryGrid>
                      <SummaryField>
                        <strong>Filter Type:</strong> {searchSummary.filter_type?.replace(/_/g, ' ')}
                      </SummaryField>
                      <SummaryField>
                        <strong>State:</strong> {capitalizeStateCode(searchSummary.state)}
                      </SummaryField>
                      <SummaryField>
                        <strong>County:</strong> {capitalizeName(searchSummary.county)}
                      </SummaryField>
                      <SummaryField>
                        <strong>Total Found:</strong> {searchSummary.total_count || totalCount}
                      </SummaryField>
                      {searchSummary.applied_filters?.min_acres > 0 && (
                        <SummaryField>
                          <strong>Min Acres:</strong> {searchSummary.applied_filters.min_acres}
                        </SummaryField>
                      )}
                      {searchSummary.applied_filters?.max_acres > 0 && (
                        <SummaryField>
                          <strong>Max Acres:</strong> {searchSummary.applied_filters.max_acres}
                        </SummaryField>
                      )}
                      {searchSummary.applied_filters?.max_assessment_per_acre > 0 && (
                        <SummaryField>
                          <strong>Max Assessment/Acre:</strong> ${searchSummary.applied_filters.max_assessment_per_acre?.toLocaleString()}
                        </SummaryField>
                      )}
                    </SummaryGrid>
                  </SummaryCard>
                )}

                {/* Results List */}
                {currentResults.map((item, index) => (
                  <ResultItem 
                    key={`${item.parcel_id || item.cluster_id || item.id || index}-${startIndex + index}`}
                    onClick={() => handleResultClick(item)}
                    title="Click to zoom to this location"
                  >
                    <ItemHeader>
                      <ItemTitle>
                        {resultType === 'parcel' ? `Parcel #${item.parcel_id}` : 'Assemblage'}
                        <TypeBadge type={resultType}>
                          {resultType === 'parcel' ? 'Single' : 'Multi'}
                        </TypeBadge>
                      </ItemTitle>
                      <AddToFavoritesButton 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the parent click
                          handleFavoriteToggle({ ...item, type: resultType });
                        }}
                      >
                        {isFavorite({ ...item, type: resultType }) ? <FaHeartBroken /> : <FaHeart />}
                      </AddToFavoritesButton>
                    </ItemHeader>
                    
                    <ItemDetails>
                      {resultType === 'parcel' ? (
                        <>
                          <ResultDetailField><strong>Size:</strong> {item.land_acres} acres</ResultDetailField>
                          <ResultDetailField><strong>Future Land Use:</strong> {item.land_use_category}</ResultDetailField>
                          <ResultDetailField><strong>Current Zoning:</strong> {item.zoning}</ResultDetailField>
                          <ResultDetailField><strong>Address:</strong> {item.addr}</ResultDetailField>
                          <ResultDetailField><strong>Owner:</strong> {item.owner}</ResultDetailField>
                          <ResultDetailField><strong>Assessed Value:</strong> ${item.assd_imp?.toLocaleString()}</ResultDetailField>
                        </>
                      ) : (
                        <>
                          <ResultDetailField><strong>Size:</strong> {item.total_acres} acres</ResultDetailField>
                          <ResultDetailField><strong>Parcels:</strong> {item.numberOfParcels}</ResultDetailField>
                          <ResultDetailField><strong>Parcel Numbers:</strong> {item.parcel_ids?.join(', ')}</ResultDetailField>
                          <ResultDetailField><strong>Future Land Use:</strong> {item.land_use_category}</ResultDetailField>
                          <ResultDetailField><strong>Current Zoning:</strong> {item.zoning}</ResultDetailField>
                          <ResultDetailField><strong>Assessed Value:</strong> ${item.assd_imp?.toLocaleString()}</ResultDetailField>
                        </>
                      )}
                    </ItemDetails>
                  </ResultItem>
                ))}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PaginationInfo>
                      Showing {startIndex + 1}-{Math.min(endIndex, results.length)} of {results.length} results
                    </PaginationInfo>
                    <PaginationControls>
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </PaginationButton>
                      <PaginationButton 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </PaginationButton>
                    </PaginationControls>
                  </PaginationContainer>
                )}
              </>
            )}
          </>
        )}
      </DetailsContent>
    </DetailsContainer>
  );
};

export default ParcelDetailsPanel; 